/*
 * pokeGenieImport.ts — parse a Poké Genie "Export scan data to CSV" file into
 * My Pokémon observations.
 *
 * The CSV is the one Poké Genie's Back Up & Restore screen produces (checked
 * against a real 3,161-row export, 2026-08-20). Header:
 *
 *   Index,Name,Form,Pokemon Number,Gender,CP,HP,Atk IV,Def IV,Sta IV,IV Avg,
 *   (Android heads the dex column "Pokemon" and has no Original Scan Date.)
 *   Level Min,Level Max,Quick Move,Charge Move,Charge Move 2,Scan Date,
 *   Original Scan Date,Catch Date,Weight,Height,Lucky,Shadow/Purified,
 *   Favorite,Dust,<per-league rank columns…>
 *
 * Mapping notes:
 *   - "Pokemon Number" is the national dex id — trusted over the name.
 *   - "Name" is the display name, which is the NICKNAME when renamed; it is
 *     stored as a nickname only when it differs from our canonical species
 *     name for that dex id.
 *   - IVs can be blank (an un-appraised scan): all three must parse or none
 *     are taken — a partial triple would fabricate certainty.
 *   - "Shadow/Purified": 0 = neither, 1 = shadow, 2 = purified (Poké Genie's
 *     encoding).
 *   - The per-league rank columns are DERIVED values; we re-derive our own
 *     from IVs, so they are ignored rather than trusted.
 *
 * The Nexus Dex template (2026-10-12, nexusDexCsvTemplate.ts) is a SUPERSET
 * of this header: Poké Genie's columns, then optional ones Poké Genie has no
 * notion of. Every lookup here is by column name, so a real Poké Genie file
 * and a template file go through the same code:
 *
 *   Shiny        0/1
 *   Costume      registry id ("pikachu-halloween-2026"), the id without the
 *                species ("halloween-2026"), or the label ("Halloween 2026");
 *                scoped to the row's species, a miss is counted and dropped
 *   Size         XXS / XS / XL / XXL
 *   Mega, Dynamax, Gigantamax   0/1
 *
 * And the `Form` column is now RESOLVED (csvFormResolve.ts): "Alola" on a
 * Marowak row becomes formId "marowak-alola", which drives the sprite and the
 * Regional dex, where it used to be stored as display text only.
 *
 * This deliberately does NOT touch the legacy `InventoryRecord` CSV pipeline
 * (`inventory.ts` / /portal/upload) — that model belongs to the abandoned
 * deep-scan era. This parser feeds `myPokemonStore` and nothing else.
 */

import { SPECIES_NAMES } from "../data/speciesNames";
import type {
  MyPokemonMoveRecord,
  MyPokemonObservation,
  MyPokemonSizeClass,
} from "./myPokemonStore";
import { starsFromIv } from "./myPokemonDerived";
import { resolveCsvCostumeId, resolveCsvFormId } from "./csvFormResolve";

export interface PokeGenieParseResult {
  observations: MyPokemonObservation[];
  /** Rows that could not be mapped (no dex id / unparseable). */
  rejected: number;
  /**
   * Rows whose Costume cell named something that species never wore (a typo,
   * or a costume the registry does not have yet). The row still imports,
   * without the costume, and the finish banner says how many so the file can
   * be fixed against the glossary.
   */
  costumesUnrecognised: number;
  /** True when the file didn't look like a Poké Genie export at all. */
  unrecognised: boolean;
  /**
   * The first few column names the file DID have (2026-09-16), so a
   * rejection can say what it saw: an Android tester's export was refused
   * and the message gave nothing to diagnose it with.
   */
  headerSample?: string[];
}

/**
 * Header matching (2026-09-16). Poké Genie's own header is fixed, but the file
 * that reaches us is not: a spreadsheet re-save can switch the delimiter to
 * ";" or a tab, add quotes, or spell "Pokémon" with its accent; so the
 * delimiter is detected off the header line and names compare on letters
 * and digits only, with the spellings we know of for the two columns
 * nothing can be mapped without.
 */
export function normaliseHeaderName(h: string): string {
  return h
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

// "Pokemon Number" is the iOS export; the ANDROID export heads the same
// column plainly "Pokemon" (owner's Galaxy export, 2026-09-16: `Index,Name,
// Form,Pokemon,Gender,CP,...`), which is why every Android import was
// refused as "not a Poké Genie export". Both spellings, then the generic ones.
const DEX_HEADERS = ["pokemonnumber", "pokemon", "pokedexnumber", "dexnumber", "nationalnumber", "dexno", "dex", "number", "pokedex"];
const CP_HEADERS = ["cp", "combatpower"];
/** The template says "Gigantamax"; the game's own shorthand is accepted too. */
const GMAX_HEADERS = ["gigantamax", "gmax"];

/** A 0/1 flag cell; "yes"/"true" are taken as 1 for hand-filled files. */
function flag(v: string | undefined): boolean {
  const s = (v ?? "").trim().toLowerCase();
  return s === "1" || s === "yes" || s === "true" || s === "y";
}

/** Poké Genie writes the glyphs; a hand-filled template may write the words. */
function gender(v: string | null): "male" | "female" | null {
  const s = (v ?? "").trim().toLowerCase();
  if (s === "♂" || s === "m" || s === "male") return "male";
  if (s === "♀" || s === "f" || s === "female") return "female";
  return null;
}

/** The Size column: the four GO size bands, any case; anything else is no size. */
function sizeClass(v: string | undefined): MyPokemonSizeClass {
  const s = (v ?? "").trim().toLowerCase();
  if (s === "xxs" || s === "xs" || s === "xl" || s === "xxl") return s;
  return null;
}

/** The delimiter the header line actually uses: the most frequent of the three. */
export function detectDelimiter(headerLine: string): "," | ";" | "\t" {
  const counts: Array<["," | ";" | "\t", number]> = [
    [",", (headerLine.match(/,/g) ?? []).length],
    [";", (headerLine.match(/;/g) ?? []).length],
    ["\t", (headerLine.match(/\t/g) ?? []).length],
  ];
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ",";
}

/** RFC-4180-ish line splitter: commas inside double quotes don't split. */
export function splitCsvLine(line: string, delimiter: string = ","): string[] {
  const out: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === delimiter) {
      out.push(field);
      field = "";
    } else {
      field += ch;
    }
  }
  out.push(field);
  return out;
}

function num(v: string | undefined): number | null {
  const s = (v ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function text(v: string | undefined): string | null {
  const s = (v ?? "").trim();
  return s.length > 0 ? s : null;
}

export function parsePokeGenieCsv(content: string): PokeGenieParseResult {
  // Strip a UTF-8 BOM (iOS text exports sometimes carry one on the header).
  const clean = content.charCodeAt(0) === 0xfeff ? content.slice(1) : content;
  const lines = clean.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    return { observations: [], rejected: 0, costumesUnrecognised: 0, unrecognised: true };
  }

  const delimiter = detectDelimiter(lines[0]);
  const rawHeader = splitCsvLine(lines[0], delimiter).map((h) => h.trim());
  const header = rawHeader.map(normaliseHeaderName);
  const col = (name: string) => header.indexOf(normaliseHeaderName(name));
  const colAny = (names: readonly string[]) => {
    for (const n of names) {
      const i = header.indexOf(n);
      if (i >= 0) return i;
    }
    return -1;
  };
  const idx = {
    name: col("Name"),
    form: col("Form"),
    dex: colAny(DEX_HEADERS),
    gender: col("Gender"),
    cp: colAny(CP_HEADERS),
    hp: col("HP"),
    atk: col("Atk IV"),
    def: col("Def IV"),
    sta: col("Sta IV"),
    quick: col("Quick Move"),
    charge1: col("Charge Move"),
    charge2: col("Charge Move 2"),
    scanDate: col("Scan Date"),
    catchDate: col("Catch Date"),
    weight: col("Weight"),
    height: col("Height"),
    lucky: col("Lucky"),
    shiny: col("Shiny"),
    shadowPurified: col("Shadow/Purified"),
    favorite: col("Favorite"),
    // Nexus Dex template columns (absent from a Poké Genie file).
    costume: col("Costume"),
    size: col("Size"),
    mega: col("Mega"),
    dynamax: col("Dynamax"),
    gmax: colAny(GMAX_HEADERS),
  };
  // The two columns nothing can be mapped without.
  if (idx.dex < 0 || idx.cp < 0) {
    return {
      observations: [],
      rejected: 0,
      costumesUnrecognised: 0,
      unrecognised: true,
      headerSample: rawHeader.filter((h) => h.length > 0).slice(0, 6),
    };
  }

  const observations: MyPokemonObservation[] = [];
  let rejected = 0;
  let costumesUnrecognised = 0;
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i], delimiter);
    const speciesId = num(cells[idx.dex]);
    if (speciesId == null || speciesId < 1 || speciesId > 1025) {
      rejected += 1;
      continue;
    }
    const canonical = SPECIES_NAMES[speciesId] ?? null;
    const printed = text(cells[idx.name]);
    const nickname =
      printed && canonical && printed.toLowerCase() !== canonical.toLowerCase()
        ? printed
        : null;

    const atk = num(cells[idx.atk]);
    const def = num(cells[idx.def]);
    const sta = num(cells[idx.sta]);
    const iv =
      atk != null && def != null && sta != null
        ? { atk, def, hp: sta, confidence: 100 }
        : null;

    const moves: MyPokemonMoveRecord[] = [];
    const quick = text(cells[idx.quick]);
    if (quick) moves.push({ name: quick, power: null, kind: "fast" });
    for (const c of [idx.charge1, idx.charge2]) {
      const m = text(cells[c]);
      if (m) moves.push({ name: m, power: null, kind: "charged" });
    }

    const genderRaw = text(cells[idx.gender]);
    const shadowPurified = num(cells[idx.shadowPurified]) ?? 0;

    // The form: Poké Genie's label resolved against the species' own forms
    // ("Alola" -> marowak-alola); "Normal" and unknown labels are the base
    // form. A Costume cell wins over the Form cell when both resolve, because
    // a costume IS the form the record wears (the sprite and the dex cell).
    const formLabel = text(cells[idx.form]);
    const formText = formLabel && formLabel.toLowerCase() !== "normal" ? formLabel : null;
    const regularFormId = formText ? resolveCsvFormId(speciesId, formText) : null;
    const costumeText = idx.costume >= 0 ? text(cells[idx.costume]) : null;
    const costumeId = costumeText ? resolveCsvCostumeId(speciesId, costumeText) : null;
    if (costumeText && !costumeId) costumesUnrecognised += 1;
    const formId = costumeId ?? regularFormId;

    observations.push({
      speciesId,
      speciesName: canonical,
      nickname,
      cp: num(cells[idx.cp]),
      hpMax: num(cells[idx.hp]),
      iv,
      appraisalStars: iv ? starsFromIv(iv) : null,
      moves,
      gender: gender(genderRaw),
      weightKg: num(cells[idx.weight]),
      heightM: num(cells[idx.height]),
      isLucky: flag(cells[idx.lucky]),
      // Not every Poké Genie export version carries a Shiny column; a
      // missing column simply imports as not-shiny.
      isShiny: idx.shiny >= 0 && flag(cells[idx.shiny]),
      isShadow: shadowPurified === 1,
      isPurified: shadowPurified === 2,
      form: formText,
      // Only set when the row names a form, so a merge into an existing
      // record never clears a form the trainer picked by hand.
      ...(formId ? { formId } : {}),
      // Template-only columns. Each is a 0/1 flag the store treats as
      // raise-only, so a Poké Genie file (no such columns) changes nothing.
      ...(idx.size >= 0 ? { sizeClass: sizeClass(cells[idx.size]) } : {}),
      ...(idx.mega >= 0 && flag(cells[idx.mega]) ? { isMega: true } : {}),
      ...(idx.dynamax >= 0 && flag(cells[idx.dynamax]) ? { isDynamax: true } : {}),
      ...(idx.gmax >= 0 && flag(cells[idx.gmax]) ? { isGmax: true } : {}),
      caughtDateRaw: text(cells[idx.catchDate]),
    });
  }
  return { observations, rejected, costumesUnrecognised, unrecognised: false };
}
